import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useGym } from '../contexts/GymContext';
import { goalAPI, dashboardAPI } from '../services/api';
import api from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Target, TrendingUp, TrendingDown, AlertCircle, CheckCircle2, Award, ArrowUp, ArrowDown, XCircle, Calendar, BarChart3, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, ComposedChart, Area, Cell } from 'recharts';
import { formatCurrency, formatPercent, formatNumber } from '../utils/formatters';

// Componente de Gauge circular
const CircularGauge = ({ percentage, isAchieved, isDecrescente, isOverLimit, size = 120 }) => {
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const displayPercentage = Math.min(percentage, 100);
  const strokeDashoffset = circumference - (displayPercentage / 100) * circumference;
  
  // Cores baseadas no status
  let strokeColor = '#22C55E'; // Verde padrão
  if (isDecrescente) {
    if (isOverLimit) {
      strokeColor = '#EF4444'; // Vermelho - ultrapassou limite
    } else {
      strokeColor = '#22C55E'; // Verde - dentro do limite
    }
  } else {
    if (isAchieved) {
      strokeColor = '#22C55E'; // Verde - meta atingida
    } else if (percentage >= 80) {
      strokeColor = '#D4A43A'; // Amarelo - quase lá
    } else if (percentage >= 50) {
      strokeColor = '#10B981'; // Azul - em progresso
    } else {
      strokeColor = '#EF4444'; // Vermelho - precisa atenção
    }
  }

  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth="8"
        />
        {/* Progress circle */}
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={strokeColor}
          strokeWidth="8"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <p className="text-2xl font-bold font-numbers" style={{ color: strokeColor }}>
            {percentage.toFixed(0)}%
          </p>
        </div>
      </div>
    </div>
  );
};

export const PerformancePage = () => {
  const { selectedGym } = useGym();
  const [goals, setGoals] = useState([]);
  const [overview, setOverview] = useState(null);
  const [performanceHistory, setPerformanceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toLocaleString('pt-BR', { month: 'long' }));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [activeView, setActiveView] = useState('current'); // 'current' or 'history'
  const [periodType, setPeriodType] = useState('mensal'); // 'mensal' or 'anual'

  const months = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
  ];

  const loadData = useCallback(async () => {
    if (!selectedGym) return;
    
    try {
      setLoading(true);
      const [goalsRes, overviewRes] = await Promise.all([
        goalAPI.getAll(selectedGym.id),
        dashboardAPI.getOverview(selectedGym.id, selectedMonth, selectedYear)
      ]);
      
      // Filter goals for selected period (mensal ou anual)
      const filteredGoals = goalsRes.data.filter(g => {
        if (periodType === 'anual') return g.period_type === 'anual' && g.year === selectedYear;
        return g.period_type !== 'anual' && g.month === selectedMonth && g.year === selectedYear;
      });
      
      setGoals(filteredGoals);
      setOverview(overviewRes.data);
    } catch (error) {
      toast.error('Erro ao carregar performance');
    } finally {
      setLoading(false);
    }
  }, [selectedGym, selectedMonth, selectedYear, periodType]);

  const loadPerformanceHistory = useCallback(async () => {
    if (!selectedGym) return;
    try {
      const response = await api.get('/performance/history', {
        params: { gym_id: selectedGym.id, year: selectedYear }
      });
      setPerformanceHistory(response.data);
    } catch (error) {
    }
  }, [selectedGym, selectedYear]);

  useEffect(() => {
    if (selectedGym) {
      loadData();
      loadPerformanceHistory();
    }
  }, [selectedGym, loadData, loadPerformanceHistory]);

  // Calcula se a meta foi atingida baseado na direção
  const isGoalAchieved = (goal) => {
    const direction = goal.direction || 'progressiva';
    if (direction === 'progressiva') {
      return goal.current_value >= goal.target_value;
    } else {
      return goal.current_value <= goal.target_value;
    }
  };

  // Calcula o progresso baseado na direção
  const calculateProgress = (goal) => {
    if (goal.target_value === 0) return 0;
    return (goal.current_value / goal.target_value) * 100;
  };

  // Analisa o status da meta considerando direção
  const getGoalStatus = (goal) => {
    const isDecrescente = (goal.direction || 'progressiva') === 'decrescente';
    const progress = calculateProgress(goal);
    const achieved = isGoalAchieved(goal);
    const isOverLimit = isDecrescente && goal.current_value > goal.target_value;

    if (isDecrescente) {
      if (isOverLimit) {
        return {
          status: 'failed',
          label: 'Ultrapassou Limite!',
          sublabel: `Excedeu R$ ${(goal.current_value - goal.target_value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
          color: 'text-[#EF4444]',
          bgColor: 'bg-[#EF4444]/10',
          borderColor: 'border-[#EF4444]/30',
          icon: XCircle,
          achieved: false,
          isOverLimit: true
        };
      } else {
        const remaining = goal.target_value - goal.current_value;
        return {
          status: 'completed',
          label: 'Dentro do Limite!',
          sublabel: `Ainda pode: R$ ${remaining.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
          color: 'text-[#22C55E]',
          bgColor: 'bg-[#22C55E]/10',
          borderColor: 'border-[#22C55E]/30',
          icon: CheckCircle2,
          achieved: true,
          isOverLimit: false
        };
      }
    } else {
      // Meta progressiva
      if (achieved) {
        return {
          status: 'completed',
          label: 'Meta Atingida!',
          sublabel: 'Objetivo alcançado',
          color: 'text-[#22C55E]',
          bgColor: 'bg-[#22C55E]/10',
          borderColor: 'border-[#22C55E]/30',
          icon: CheckCircle2,
          achieved: true,
          isOverLimit: false
        };
      } else if (progress >= 80) {
        return {
          status: 'near',
          label: 'Quase Lá!',
          sublabel: `Falta: R$ ${(goal.target_value - goal.current_value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
          color: 'text-[#D4A43A]',
          bgColor: 'bg-[#D4A43A]/10',
          borderColor: 'border-[#D4A43A]/30',
          icon: TrendingUp,
          achieved: false,
          isOverLimit: false
        };
      } else if (progress >= 50) {
        return {
          status: 'progress',
          label: 'Em Progresso',
          sublabel: `Falta: R$ ${(goal.target_value - goal.current_value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
          color: 'text-[#10B981]',
          bgColor: 'bg-[#10B981]/10',
          borderColor: 'border-[#10B981]/30',
          icon: Target,
          achieved: false,
          isOverLimit: false
        };
      } else {
        return {
          status: 'low',
          label: 'Precisa Atenção',
          sublabel: `Falta: R$ ${(goal.target_value - goal.current_value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
          color: 'text-[#EF4444]',
          bgColor: 'bg-[#EF4444]/10',
          borderColor: 'border-[#EF4444]/30',
          icon: AlertCircle,
          achieved: false,
          isOverLimit: false
        };
      }
    }
  };

  // Calcula performance geral considerando direção
  const calculateOverallPerformance = () => {
    if (goals.length === 0) return { achieved: 0, total: 0, percentage: 0 };
    
    const achievedGoals = goals.filter(g => isGoalAchieved(g)).length;
    return {
      achieved: achievedGoals,
      total: goals.length,
      percentage: (achievedGoals / goals.length) * 100
    };
  };

  const overallStats = calculateOverallPerformance();

  // Separa metas progressivas e decrescentes
  const progressiveGoals = goals.filter(g => (g.direction || 'progressiva') === 'progressiva');
  const decrescenteGoals = goals.filter(g => g.direction === 'decrescente');

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-[#22C55E] text-xl font-heading">Carregando...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Header com saudação e filtros */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-2">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
              <Award className="text-emerald-400" size={36} />
              Performance
            </h1>
            <p className="text-zinc-400 mt-2 text-base">Acompanhe o desempenho das suas metas e orçamentos {periodType === 'anual' ? `— Ano ${selectedYear}` : `— ${selectedMonth.charAt(0).toUpperCase() + selectedMonth.slice(1)}/${selectedYear}`}</p>
          </div>
          
          {/* Filtros */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* View Toggle */}
            <div className="flex p-1 rounded-lg bg-zinc-800/50 border border-zinc-700">
              <button
                onClick={() => setActiveView('current')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                  activeView === 'current'
                    ? 'bg-emerald-600 text-white'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                <Target size={16} />
                Mês Atual
              </button>
              <button
                onClick={() => setActiveView('history')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                  activeView === 'history'
                    ? 'bg-emerald-600 text-white'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                <Activity size={16} />
                Evolução Anual
              </button>
            </div>
            
            {activeView === 'current' && (
              <div className="flex p-1 rounded-lg bg-zinc-800/50 border border-zinc-700" data-testid="performance-period-toggle">
                <button
                  data-testid="performance-period-mensal"
                  onClick={() => setPeriodType('mensal')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${periodType === 'mensal' ? 'bg-emerald-600 text-white' : 'text-zinc-400 hover:text-white'}`}
                >
                  Mensal
                </button>
                <button
                  data-testid="performance-period-anual"
                  onClick={() => setPeriodType('anual')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${periodType === 'anual' ? 'bg-emerald-600 text-white' : 'text-zinc-400 hover:text-white'}`}
                >
                  Anual
                </button>
              </div>
            )}

            {activeView === 'current' && periodType === 'mensal' && (
              <select
                data-testid="month-selector"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="h-10 px-4 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-emerald-500 focus:outline-none"
              >
                {months.map(month => (
                  <option key={month} value={month}>{month.charAt(0).toUpperCase() + month.slice(1)}</option>
                ))}
              </select>
            )}
            <select
              data-testid="year-selector"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="h-10 px-4 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-emerald-500 focus:outline-none"
            >
              {[2024, 2025, 2026].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>

        {/* History View */}
        {activeView === 'history' && (
          <div className="space-y-6">
            {/* Annual Performance Summary */}
            <Card className="glass-panel border-2 border-[#22C55E]/20">
              <CardHeader>
                <CardTitle className="text-xl text-white flex items-center gap-2">
                  <Calendar className="text-[#22C55E]" size={24} />
                  Evolução da Performance - {selectedYear}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {performanceHistory.filter(m => m.has_data || m.total_goals > 0).length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <BarChart3 size={48} className="mx-auto mb-4 opacity-50" />
                    <p>Nenhum dado de performance para {selectedYear}</p>
                    <p className="text-sm mt-2">Adicione metas e registre dados financeiros para ver a evolução</p>
                  </div>
                ) : (
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={performanceHistory.filter(m => m.has_data || m.total_goals > 0)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis 
                          dataKey="month_short" 
                          stroke="#888" 
                          tick={{ fill: '#888', fontSize: 12 }}
                        />
                        <YAxis 
                          yAxisId="left"
                          stroke="#888" 
                          tick={{ fill: '#888', fontSize: 12 }}
                          domain={[0, 100]}
                          tickFormatter={(value) => `${value}%`}
                        />
                        <YAxis 
                          yAxisId="right"
                          orientation="right"
                          stroke="#888" 
                          tick={{ fill: '#888', fontSize: 12 }}
                          tickFormatter={(value) => formatCurrency(value)}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1a1a1a', 
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '8px'
                          }}
                          labelStyle={{ color: '#fff' }}
                          formatter={(value, name) => {
                            if (name === 'Performance') return [`${value.toFixed(0)}%`, name];
                            return [`R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, name];
                          }}
                        />
                        <Legend />
                        <Area 
                          yAxisId="right"
                          type="linear" 
                          dataKey="revenue" 
                          name="Faturamento"
                          fill="#22C55E20" 
                          stroke="#22C55E"
                          strokeWidth={2}
                        />
                        <Area 
                          yAxisId="right"
                          type="linear" 
                          dataKey="expenses" 
                          name="Despesas"
                          fill="#EF444420" 
                          stroke="#EF4444"
                          strokeWidth={2}
                        />
                        <Line 
                          yAxisId="left"
                          type="linear" 
                          dataKey="performance" 
                          name="Performance"
                          stroke="#10B981"
                          strokeWidth={3}
                          dot={{ fill: '#10B981', strokeWidth: 2, r: 6 }}
                          activeDot={{ r: 8 }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Gráfico Anual de Orçamento: Gasto Real x Teto */}
            {performanceHistory.some(m => m.orcamento_teto > 0) && (
              <Card className="glass-panel border-2 border-[#F59E0B]/20" data-testid="orcamento-anual-chart">
                <CardHeader>
                  <CardTitle className="text-xl text-white flex items-center gap-2">
                    <ArrowDown className="text-[#F59E0B]" size={22} />
                    Orçamento Anual — Gasto Real x Teto ({selectedYear})
                  </CardTitle>
                  <p className="text-zinc-400 text-sm mt-1">Comparação mês a mês entre o que foi gasto e o teto de despesas que você orçou.</p>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={performanceHistory.filter(m => m.orcamento_teto > 0 || m.expenses > 0)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis dataKey="month_short" stroke="#888" tick={{ fill: '#888', fontSize: 12 }} />
                        <YAxis stroke="#888" tick={{ fill: '#888', fontSize: 12 }} tickFormatter={(value) => formatCurrency(value)} width={70} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                          labelStyle={{ color: '#fff' }}
                          formatter={(value) => `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                        />
                        <Legend />
                        <Bar dataKey="expenses" name="Gasto Real" radius={[4, 4, 0, 0]}>
                          {performanceHistory.filter(m => m.orcamento_teto > 0 || m.expenses > 0).map((m, i) => (
                            <Cell key={i} fill={m.orcamento_teto > 0 && m.expenses > m.orcamento_teto ? '#EF4444' : '#22C55E'} />
                          ))}
                        </Bar>
                        <Line type="linear" dataKey="orcamento_teto" name="Teto (Orçado)" stroke="#F59E0B" strokeWidth={3} strokeDasharray="6 4" dot={{ fill: '#F59E0B', r: 4 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-zinc-400">
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#22C55E] inline-block" /> Dentro do teto</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#EF4444] inline-block" /> Estourou o teto</span>
                    <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-[#F59E0B] inline-block" /> Teto orçado (Despesas)</span>
                  </div>
                </CardContent>
              </Card>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {performanceHistory.map((monthData) => {
                const hasGoals = monthData.total_goals > 0;
                const performance = monthData.performance;
                
                let performanceColor = 'text-zinc-500';
                let bgColor = 'bg-white/5';
                let borderColor = 'border-white/10';
                
                if (hasGoals) {
                  if (performance >= 80) {
                    performanceColor = 'text-[#22C55E]';
                    bgColor = 'bg-[#22C55E]/5';
                    borderColor = 'border-[#22C55E]/30';
                  } else if (performance >= 50) {
                    performanceColor = 'text-[#D4A43A]';
                    bgColor = 'bg-[#D4A43A]/5';
                    borderColor = 'border-[#D4A43A]/30';
                  } else {
                    performanceColor = 'text-[#EF4444]';
                    bgColor = 'bg-[#EF4444]/5';
                    borderColor = 'border-[#EF4444]/30';
                  }
                }
                
                return (
                  <Card 
                    key={monthData.month} 
                    className={`${bgColor} border ${borderColor} hover:scale-105 transition-transform cursor-pointer`}
                    onClick={() => {
                      setSelectedMonth(monthData.month);
                      setActiveView('current');
                    }}
                  >
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">
                        {monthData.month_short}/{selectedYear.toString().slice(-2)}
                      </p>
                      
                      {hasGoals ? (
                        <>
                          <div className="relative w-16 h-16 mx-auto mb-2">
                            <CircularGauge 
                              percentage={performance}
                              isAchieved={performance >= 100}
                              isDecrescente={false}
                              isOverLimit={false}
                              size={64}
                            />
                          </div>
                          <p className={`text-lg font-bold ${performanceColor}`}>
                            {monthData.achieved_goals}/{monthData.total_goals}
                          </p>
                          <p className="text-[10px] text-zinc-500">metas atingidas</p>
                        </>
                      ) : monthData.has_data ? (
                        <>
                          <div className="w-16 h-16 mx-auto mb-2 flex items-center justify-center">
                            <BarChart3 size={32} className="text-zinc-600" />
                          </div>
                          <p className="text-sm text-zinc-500">Sem metas</p>
                          <p className="text-[10px] text-zinc-600">Dados financeiros</p>
                        </>
                      ) : (
                        <>
                          <div className="w-16 h-16 mx-auto mb-2 flex items-center justify-center">
                            <Calendar size={32} className="text-zinc-700" />
                          </div>
                          <p className="text-sm text-zinc-600">Sem dados</p>
                        </>
                      )}
                      
                      {/* Financial mini summary */}
                      {monthData.has_data && (
                        <div className="mt-2 pt-2 border-t border-white/5">
                          <div className="flex justify-between text-[9px]">
                            <span className="text-[#22C55E]">+{formatCurrency(monthData.revenue)}</span>
                            <span className="text-[#EF4444]">-{formatCurrency(monthData.expenses)}</span>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Detailed Stats Table */}
            <Card className="glass-panel">
              <CardHeader>
                <CardTitle className="text-xl text-white flex items-center gap-2">
                  <BarChart3 className="text-[#10B981]" size={24} />
                  Detalhamento Mensal
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-4 text-zinc-400 font-medium">Mês</th>
                        <th className="text-center py-3 px-4 text-zinc-400 font-medium">Performance</th>
                        <th className="text-center py-3 px-4 text-zinc-400 font-medium">Progressivas</th>
                        <th className="text-center py-3 px-4 text-zinc-400 font-medium">Decrescentes</th>
                        <th className="text-right py-3 px-4 text-zinc-400 font-medium">Faturamento</th>
                        <th className="text-right py-3 px-4 text-zinc-400 font-medium">Despesas</th>
                        <th className="text-right py-3 px-4 text-zinc-400 font-medium">Lucro</th>
                      </tr>
                    </thead>
                    <tbody>
                      {performanceHistory.filter(m => m.has_data || m.total_goals > 0).map((monthData) => (
                        <tr 
                          key={monthData.month} 
                          className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                          onClick={() => {
                            setSelectedMonth(monthData.month);
                            setActiveView('current');
                          }}
                        >
                          <td className="py-3 px-4 text-white font-medium capitalize">{monthData.month}</td>
                          <td className="py-3 px-4 text-center">
                            {monthData.total_goals > 0 ? (
                              <span className={`font-bold ${
                                monthData.performance >= 80 ? 'text-[#22C55E]' : 
                                monthData.performance >= 50 ? 'text-[#D4A43A]' : 'text-[#EF4444]'
                              }`}>
                                {monthData.performance.toFixed(0)}%
                              </span>
                            ) : (
                              <span className="text-zinc-600">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {monthData.progressive.total > 0 ? (
                              <span className="text-[#22C55E]">
                                {monthData.progressive.achieved}/{monthData.progressive.total}
                              </span>
                            ) : (
                              <span className="text-zinc-600">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {monthData.decrescente.total > 0 ? (
                              <span className="text-[#EF4444]">
                                {monthData.decrescente.achieved}/{monthData.decrescente.total}
                              </span>
                            ) : (
                              <span className="text-zinc-600">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right text-[#22C55E] font-numbers">
                            {monthData.revenue > 0 ? `R$ ${monthData.revenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
                          </td>
                          <td className="py-3 px-4 text-right text-[#EF4444] font-numbers">
                            {monthData.expenses > 0 ? `R$ ${monthData.expenses.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
                          </td>
                          <td className={`py-3 px-4 text-right font-numbers font-bold ${monthData.profit >= 0 ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>
                            {monthData.revenue > 0 || monthData.expenses > 0 
                              ? `R$ ${monthData.profit.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                              : '-'
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Current Month View */}
        {activeView === 'current' && (
          <>
            {/* Overall Performance Card */}
            <Card className="glass-panel border-2 border-[#22C55E]/20">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                  <div className="flex items-center gap-6">
                    <CircularGauge 
                      percentage={overallStats.percentage} 
                      isAchieved={overallStats.percentage === 100}
                      isDecrescente={false}
                      isOverLimit={false}
                      size={160}
                    />
                    <div>
                      <p className="text-zinc-400 text-sm uppercase tracking-wider mb-2">Performance Geral</p>
                      <p className="text-5xl font-bold text-[#22C55E] font-numbers">{overallStats.percentage.toFixed(0)}%</p>
                      <p className="text-zinc-500 mt-2">
                        {overallStats.achieved} de {overallStats.total} metas atingidas
                      </p>
                    </div>
                  </div>
                  
                  {/* Resumo por tipo */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl bg-[#22C55E]/10 border border-[#22C55E]/20">
                      <div className="flex items-center gap-2 mb-2">
                        <ArrowUp className="text-[#22C55E]" size={18} />
                        <span className="text-zinc-400 text-xs uppercase">Metas</span>
                      </div>
                      <p className="text-2xl font-bold text-white font-numbers">
                        {progressiveGoals.filter(g => isGoalAchieved(g)).length}/{progressiveGoals.length}
                      </p>
                    </div>
                    <div className="p-4 rounded-xl bg-[#EF4444]/10 border border-[#EF4444]/20">
                      <div className="flex items-center gap-2 mb-2">
                        <ArrowDown className="text-[#EF4444]" size={18} />
                        <span className="text-zinc-400 text-xs uppercase">Orçamento</span>
                      </div>
                      <p className="text-2xl font-bold text-white font-numbers">
                        {decrescenteGoals.filter(g => isGoalAchieved(g)).length}/{decrescenteGoals.length}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Goals Performance Grid */}
            {goals.length === 0 ? (
              <Card className="glass-panel">
                <CardContent className="p-16 text-center">
                  <Target className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-500 text-lg">Nenhuma meta ou orçamento para este período</p>
                  <p className="text-zinc-600 text-sm mt-2">Vá até "Metas & Orçamento" para definir seus objetivos e tetos de gasto ({periodType === 'anual' ? 'anuais' : 'mensais'})</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Metas Progressivas */}
                {progressiveGoals.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-heading font-bold text-white uppercase flex items-center gap-2">
                      <ArrowUp className="text-[#22C55E]" size={24} />
                      Metas
                      <span className="text-sm text-zinc-500 font-normal lowercase ml-2">(objetivos de resultado — quer atingir ou ultrapassar)</span>
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {progressiveGoals.map(goal => {
                        const status = getGoalStatus(goal);
                        const progress = calculateProgress(goal);
                        const StatusIcon = status.icon;
                        
                        return (
                          <Card key={goal.id} className={`glass-panel border-2 ${status.borderColor} hover:scale-[1.01] transition-transform`}>
                            <CardContent className="p-6">
                              <div className="flex items-start gap-3 sm:gap-4">
                                {/* Gauge */}
                                <CircularGauge 
                                  percentage={progress}
                                  isAchieved={status.achieved}
                                  isDecrescente={false}
                                  isOverLimit={false}
                                  size={84}
                                />
                                
                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="px-2 py-1 rounded-md bg-[#10B981]/10 text-[#10B981] text-xs font-medium uppercase">
                                      {goal.goal_type}
                                    </span>
                                    <span className="px-2 py-1 rounded-md bg-[#22C55E]/10 text-[#22C55E] text-xs font-medium uppercase flex items-center gap-1">
                                      <ArrowUp size={10} />
                                      Progressiva
                                    </span>
                                  </div>
                                  <h3 className="text-base sm:text-lg font-bold text-white mb-2 break-words">{goal.name}</h3>
                                  
                                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 mb-3">
                                    <span className="text-xl sm:text-2xl font-bold font-numbers text-white whitespace-nowrap">
                                      R$ {goal.current_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </span>
                                    <span className="text-zinc-500">/</span>
                                    <span className="text-zinc-400 font-numbers whitespace-nowrap text-sm sm:text-base">
                                      R$ {goal.target_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                  
                                  {/* Status Badge */}
                                  <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg ${status.bgColor}`}>
                                    <StatusIcon className={status.color} size={18} />
                                    <div>
                                      <p className={`font-bold text-sm ${status.color}`}>{status.label}</p>
                                      <p className="text-zinc-400 text-xs">{status.sublabel}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Metas Decrescentes */}
                {decrescenteGoals.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-heading font-bold text-white uppercase flex items-center gap-2">
                      <ArrowDown className="text-[#EF4444]" size={24} />
                      Orçamento
                      <span className="text-sm text-zinc-500 font-normal lowercase ml-2">(teto de gasto — quer ficar abaixo do limite)</span>
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {decrescenteGoals.map(goal => {
                        const status = getGoalStatus(goal);
                        const progress = calculateProgress(goal);
                        const StatusIcon = status.icon;
                        
                        return (
                          <Card key={goal.id} className={`glass-panel border-2 ${status.borderColor} hover:scale-[1.01] transition-transform`}>
                            <CardContent className="p-6">
                              <div className="flex items-start gap-3 sm:gap-4">
                                {/* Gauge */}
                                <CircularGauge 
                                  percentage={progress}
                                  isAchieved={status.achieved}
                                  isDecrescente={true}
                                  isOverLimit={status.isOverLimit}
                                  size={84}
                                />
                                
                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="px-2 py-1 rounded-md bg-[#10B981]/10 text-[#10B981] text-xs font-medium uppercase">
                                      {goal.goal_type}
                                    </span>
                                    <span className="px-2 py-1 rounded-md bg-[#EF4444]/10 text-[#EF4444] text-xs font-medium uppercase flex items-center gap-1">
                                      <ArrowDown size={10} />
                                      Decrescente
                                    </span>
                                  </div>
                                  <h3 className="text-base sm:text-lg font-bold text-white mb-2 break-words">{goal.name}</h3>
                                  
                                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 mb-3">
                                    <span className={`text-xl sm:text-2xl font-bold font-numbers whitespace-nowrap ${status.isOverLimit ? 'text-[#EF4444]' : 'text-white'}`}>
                                      R$ {goal.current_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </span>
                                    <span className="text-zinc-500">/</span>
                                    <span className="text-zinc-400 font-numbers whitespace-nowrap text-sm sm:text-base">
                                      máx R$ {goal.target_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                  
                                  {/* Status Badge */}
                                  <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg ${status.bgColor}`}>
                                    <StatusIcon className={status.color} size={18} />
                                    <div>
                                      <p className={`font-bold text-sm ${status.color}`}>{status.label}</p>
                                      <p className="text-zinc-400 text-xs">{status.sublabel}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};
